import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  SxProps,
  CardHeader,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
} from '@mui/icons-material';

import { useAuth } from 'hooks/AuthContext';
import { useAppState } from 'hooks/GlobalContext';
import { StyledMarkdown } from 'components/StyledMarkdown';
import { Resume } from 'types/types';
import { useNavigate } from 'react-router-dom';

interface ResumeInfoProps {
  resume: Resume;
  sx?: SxProps;
  action?: string;
  elevation?: number;
  variant?: 'minimal' | 'small' | 'normal' | 'all' | null;
}

const ResumeInfo: React.FC<ResumeInfoProps> = (props: ResumeInfoProps) => {
  const { setSnack } = useAppState();
  const navigate = useNavigate();
  const { resume } = props;
  const { user, apiClient } = useAuth();
  const { sx, variant = 'normal' } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) || variant === 'minimal';
  const isAdmin = user?.isAdmin;
  const [activeResume, setActiveResume] = useState<Resume>({ ...resume });
  const [deleted, setDeleted] = useState<boolean>(false);

  useEffect(() => {
    if (resume && resume.id !== activeResume?.id) {
      setActiveResume(resume);
    }
  }, [resume, activeResume]);

  const deleteResume = async (id: string | undefined): Promise<void> => {
    if (id) {
      try {
        await apiClient.deleteResume(id);
        setDeleted(true);
        setSnack('Resume deleted successfully.');
      } catch (error) {
        setSnack('Failed to delete resume.');
      }
    }
  };

  const handleReset = async (): Promise<void> => {
    setActiveResume({ ...resume });
  };

  const handleEditOpen = (): void => {
    navigate(`/candidate/resumes/${activeResume.id}/edit`);
  };

  if (!resume) {
    return <Box>No resume provided.</Box>;
  }

  const formatDate = (date: Date | undefined): string => {
    if (!date) return 'N/A';
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  return (
    <Box
      className="ResumeInfo"
      sx={{
        display: 'flex',
        borderColor: 'transparent',
        borderWidth: 2,
        borderStyle: 'solid',
        transition: 'all 0.3s ease',
        flexDirection: 'column',
        minWidth: 0,
        opacity: deleted ? 0.5 : 1.0,
        backgroundColor: deleted
          ? theme.palette.action.disabledBackground
          : theme.palette.background.paper,
        pointerEvents: deleted ? 'none' : 'auto',
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          p: 1,
          pb: 0,
          height: '100%',
          flexDirection: 'column',
          alignItems: 'stretch',
          position: 'relative',
        }}
      >
        {/* Header Information */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            {activeResume.candidate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold">
                  Candidate
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary">
              {activeResume.candidate?.fullName || activeResume.candidateId}
            </Typography>

            {activeResume.job && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <WorkIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Job
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {activeResume.job.title} at {activeResume.job.company}
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight="bold">
                Timeline
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Created: {formatDate(activeResume.createdAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Updated: {formatDate(activeResume.updatedAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Job ID: {activeResume.job?.id}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}
            >
              Resume ID:
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  navigate(`/chat/${activeResume.id}`);
                }}
              >
                {activeResume.id}
              </Box>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Resume Content */}
        {activeResume.resume && (
          <Card elevation={0} sx={{ m: 0, p: 0, background: 'transparent !important' }}>
            <CardHeader
              title="Resume Content"
              avatar={<DescriptionIcon color="success" />}
              sx={{ p: 0, pb: 1 }}
              action={
                <Tooltip title="Edit Resume">
                  <IconButton size="small" onClick={handleEditOpen}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            {variant === 'all' && activeResume.resume && (
              <CardContent sx={{ p: 0 }}>
                <StyledMarkdown content={activeResume.resume} />
              </CardContent>
            )}
          </Card>
        )}
      </Box>

      {/* Admin Controls */}
      {isAdmin && (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              pl: 1,
              pr: 1,
              gap: 1,
              alignContent: 'center',
              height: '32px',
            }}
          >
            <Tooltip title="Edit Resume">
              <IconButton
                size="small"
                onClick={(e): void => {
                  e.stopPropagation();
                  handleEditOpen();
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete Resume">
              <IconButton
                size="small"
                onClick={(e): void => {
                  e.stopPropagation();
                  deleteResume(activeResume.id);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Reset Resume">
              <IconButton
                size="small"
                onClick={(e): void => {
                  e.stopPropagation();
                  handleReset();
                }}
              >
                <RestoreIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export { ResumeInfo };
